import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FiInfo } from 'react-icons/fi';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { equipmentTimeCardsService } from '../services/api/equipmentTimeCardsService';
import { laborScopeCodesService } from '../services/api/laborScopeCodesService';
import { stockInventoryItemsService } from '../services/api/stockInventoryItemsService';

/** Body scroll lock while an excel upload modal is open. */
function usePpUploadModalBodyLock() {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
}

/**
 * Excel upload modal shell:
 * - Desktop / website: compact centered card (original size)
 * - Mobile portrait: content-height card (no empty stretch)
 * - Mobile landscape: near-fullscreen inset (unchanged / preferred)
 * Uses max-lg (1024) so landscape phones (e.g. 844px wide) still get landscape rules.
 */
export function PpExcelUploadModalShell({ title, onClose, onDragOver, onDrop, children }) {
  usePpUploadModalBodyLock();

  return createPortal(
    <div
      className="fixed inset-0 z-[10160] bg-black/50 overflow-y-auto max-lg:landscape:overflow-hidden"
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-8 max-lg:px-2 max-lg:py-6 max-lg:landscape:block max-lg:landscape:h-[100dvh] max-lg:landscape:min-h-0 max-lg:landscape:p-0">
        <div
          className={[
            'flex w-[820px] max-w-[95vw] max-h-[calc(100dvh-4rem)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl',
            /* Portrait mobile: same L/R inset as landscape (inset-2 = 0.5rem); slightly taller content card */
            'max-lg:w-full max-lg:max-w-none max-lg:max-h-[min(90dvh,640px)]',
            /* Landscape mobile: full inset over app bar + bottom nav */
            'max-lg:landscape:absolute max-lg:landscape:inset-2 max-lg:landscape:h-auto max-lg:landscape:max-h-none max-lg:landscape:w-auto max-lg:landscape:max-w-none',
          ].join(' ')}
        >
          <div className="relative z-10 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 max-lg:px-4 max-lg:py-3 shadow-[0_2px_5px_rgba(0,0,0,0.08)]">
            <div className="text-lg max-lg:text-base font-semibold text-gray-800 font-poppins max-lg:landscape:text-[20px] max-lg:landscape:font-bold max-lg:landscape:text-[#2C2C2C]">
              {title}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              title="Close"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* shrink (not grow) so portrait/desktop stay content-sized; landscape flex-1 fills inset */}
          <div className="min-h-0 shrink overflow-y-auto p-5 max-lg:p-3 max-lg:landscape:flex-1 [-webkit-overflow-scrolling:touch]">
            {children}
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t px-5 py-4 max-lg:px-4 max-lg:py-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 max-lg:px-3 max-lg:py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const ACCENT_STYLES = {
  green: {
    fileName: 'text-green-700',
    button: 'bg-green-700 text-white text-sm hover:bg-green-800 disabled:opacity-60',
  },
  violet: {
    fileName: 'text-violet-700',
    button: 'bg-violet-700 text-white text-sm hover:bg-violet-800 disabled:opacity-60',
  },
  teal: {
    fileName: 'text-teal-700',
    button: 'bg-teal-700 text-white text-sm hover:bg-teal-800 disabled:opacity-60',
  },
  red: {
    fileName: 'text-red-700',
    button: 'bg-red-700 text-white text-sm hover:bg-red-800 disabled:opacity-60',
  },
};

function isExcelFile(file) {
  const name = file?.name?.toLowerCase?.() || '';
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result && result.indexOf('base64,') >= 0 ? result.split('base64,')[1] : result;
      resolve(base64 || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Shared Excel master-sheet upload UI (file list + drag/drop + choose files).
 * variant: "modal" wraps in PpExcelUploadModalShell; "inline" renders panel content only.
 */
export function MasterExcelUploadPanel({
  uploadPath,
  successFallbackMessage,
  accent = 'green',
  validateBeforeUpload,
  onUploadSuccess,
  variant = 'inline',
  title,
  onClose,
  /** When true, accept Excel files into the local list only — no API call. */
  uiOnly = false,
}) {
  const { getAuthConfig } = useAuthFetch();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const fileInputRef = useRef(null);

  const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
  const styles = ACCENT_STYLES[accent] || ACCENT_STYLES.green;

  const uploadFile = async (file) => {
    if (!isExcelFile(file)) return;
    if (typeof validateBeforeUpload === 'function') {
      const validationError = validateBeforeUpload(file);
      if (validationError) {
        setUploadError(validationError);
        return;
      }
    }
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    try {
      // Frontend-only preview: show selected file without calling the backend.
      if (uiOnly || !uploadPath) {
        setUploadSuccess(successFallbackMessage || 'File selected. Backend upload will be wired later.');
        setFiles((prev) => [...prev, { name: file.name, modified: new Date().toLocaleString() }]);
        if (typeof onUploadSuccess === 'function') onUploadSuccess({ status: 'ui-only', fileName: file.name });
        return;
      }

      const fileBase64 = await readFileAsBase64(file);
      const response = await axios.post(
        `${baseUrl}${uploadPath}`,
        { fileBase64 },
        await getAuthConfig({ headers: { 'Content-Type': 'application/json' } })
      );
      if (response.data.status === 'success') {
        setUploadSuccess(response.data.message || successFallbackMessage);
        setFiles((prev) => [...prev, { name: file.name, modified: new Date().toLocaleString() }]);
        if (typeof onUploadSuccess === 'function') onUploadSuccess(response.data);
      } else {
        setUploadError(response.data.message || 'Upload failed.');
      }
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fileList = e.dataTransfer?.files;
    if (fileList?.length) {
      const file = Array.from(fileList).find(isExcelFile);
      if (file) uploadFile(file);
    }
  };

  const onFileSelect = (e) => {
    const fileList = e.target?.files;
    if (fileList?.length) {
      const file = Array.from(fileList).find(isExcelFile);
      if (file) uploadFile(file);
    }
    e.target.value = '';
  };

  const content = (
    <>
      <div className="border rounded-md overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 flex">
          <div className="w-2/3">Name</div>
          <div className="w-1/3">Modified</div>
        </div>
        <div className="max-h-64 max-md:max-h-40 overflow-auto divide-y">
          {uploadError && (
            <div className="px-4 py-2 text-sm text-red-600 bg-red-50">{uploadError}</div>
          )}
          {uploadSuccess && (
            <div className="px-4 py-2 text-sm text-green-700 bg-green-50">{uploadSuccess}</div>
          )}
          {files.length === 0 && !uploadSuccess ? (
            <div className="px-4 py-6 text-sm text-gray-500">No files yet.</div>
          ) : (
            files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="px-4 py-3 text-sm flex">
                <div className="w-2/3 truncate">
                  <span className={styles.fileName}>{f.name}</span>
                </div>
                <div className="w-1/3 text-gray-600">{f.modified}</div>
              </div>
            ))
          )}
        </div>
      </div>

      <div
        className="mt-6 max-md:mt-3 h-40 max-md:h-32 rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 max-md:text-sm"
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={onDrop}
      >
        <div className="mb-2">Drag and drop files here</div>
        <div className="text-xs mb-3">or</div>
        <button
          type="button"
          className={`px-3 py-1.5 rounded ${styles.button}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Choose files'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".xlsx,.xls"
          className="hidden"
          onChange={onFileSelect}
        />
      </div>
    </>
  );

  if (variant === 'modal') {
    return (
      <PpExcelUploadModalShell
        title={title}
        onClose={onClose}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        {content}
      </PpExcelUploadModalShell>
    );
  }

  return content;
}

const LABOR_SCOPE_UPLOAD_PATH = '/api/miter/production-performance/labor-scope-codes/upload';
const STOCK_INVENTORY_UPLOAD_PATH = '/api/miter/production-performance/stock-inventory-items/upload';
const EQUIPMENT_TIME_CARDS_UPLOAD_PATH = '/api/miter/production-performance/equipment-time-cards/upload';

const COLUMN_HINT_TONES = {
  green: {
    box: 'border-green-200 bg-green-50/80',
    iconWrap: 'bg-green-100 text-green-700',
    title: 'text-green-900',
    chip: 'border-green-200 text-green-800',
    index: 'text-green-500',
  },
  teal: {
    box: 'border-teal-200 bg-teal-50/80',
    iconWrap: 'bg-teal-100 text-teal-700',
    title: 'text-teal-900',
    chip: 'border-teal-200 text-teal-800',
    index: 'text-teal-500',
  },
  violet: {
    box: 'border-violet-200 bg-violet-50/80',
    iconWrap: 'bg-violet-100 text-violet-700',
    title: 'text-violet-900',
    chip: 'border-violet-200 text-violet-800',
    index: 'text-violet-500',
  },
};

function UploadColumnHint({ tone, pageName, columns }) {
  const styles = COLUMN_HINT_TONES[tone] || COLUMN_HINT_TONES.green;
  return (
    <div className={`mb-5 rounded-xl border px-4 py-3.5 ${styles.box}`}>
      <div className="flex gap-3">
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${styles.iconWrap}`}>
          <FiInfo size={15} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${styles.title}`}>Match these column headers, in this order</p>
          <p className="mt-1 text-sm leading-5 text-slate-600">
            The first row of your Excel file must use the same names as {pageName}.
          </p>
          {columns.length > 0 ? (
            <ol className="mt-3 flex flex-wrap gap-2">
              {columns.map((label, index) => (
                <li
                  key={`${label}-${index}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 text-xs font-medium ${styles.chip}`}
                >
                  <span className={`tabular-nums ${styles.index}`}>{index + 1}</span>
                  {label}
                </li>
              ))}
            </ol>
          ) : null}
          <p className="mt-3 text-xs leading-5 text-slate-500">
            Tip: download the current sheet from {pageName} and use that file as your template after you rename, add, or delete a column.
          </p>
        </div>
      </div>
    </div>
  );
}

function useVisibleColumnLabels(loadColumns) {
  const [expectedColumns, setExpectedColumns] = useState([]);

  useEffect(() => {
    let cancelled = false;
    loadColumns()
      .then((response) => {
        if (cancelled) return;
        const columns = Array.isArray(response?.data) ? response.data : [];
        setExpectedColumns(columns.filter((col) => col.visible).map((col) => col.label).filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setExpectedColumns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [loadColumns]);

  return expectedColumns;
}

/** Inline panel for Configuration Engine. */
export function LaborScopeCodesUploadInline() {
  const expectedColumns = useVisibleColumnLabels(laborScopeCodesService.listColumns);

  return (
    <>
      <UploadColumnHint
        tone="green"
        pageName="Manage Labor & Scope Codes"
        columns={expectedColumns}
      />
      <MasterExcelUploadPanel
        uploadPath={LABOR_SCOPE_UPLOAD_PATH}
        successFallbackMessage="Labor & Scope Codes uploaded successfully."
        accent="green"
      />
    </>
  );
}

/** Inline panel for Configuration Engine. */
export function StockInventoryItemsUploadInline() {
  const expectedColumns = useVisibleColumnLabels(stockInventoryItemsService.listColumns);

  return (
    <>
      <UploadColumnHint
        tone="violet"
        pageName="Manage Stock Inventory Items"
        columns={expectedColumns}
      />
      <MasterExcelUploadPanel
        uploadPath={STOCK_INVENTORY_UPLOAD_PATH}
        successFallbackMessage="Stock inventory items uploaded successfully."
        accent="violet"
      />
    </>
  );
}

/** Inline panel for Configuration Engine — Upload Equipment Time Cards. */
export function EquipmentTimeCardsUploadInline() {
  const expectedColumns = useVisibleColumnLabels(equipmentTimeCardsService.listColumns);

  return (
    <>
      <UploadColumnHint
        tone="teal"
        pageName="Manage Equipment Time Cards"
        columns={expectedColumns}
      />
      <MasterExcelUploadPanel
        uploadPath={EQUIPMENT_TIME_CARDS_UPLOAD_PATH}
        successFallbackMessage="Equipment time cards uploaded successfully."
        accent="teal"
      />
    </>
  );
}
